import React, { useState, useEffect, useRef } from 'react';
import { Edit, ChevronDown, ChevronRight, RefreshCw, MoreVertical, Copy, Trash2, FolderCode, ExternalLink } from 'lucide-react';
import './JobRow.css';
import { format } from 'date-fns';
import FailureReasonModal, { getFailureSummaryLabel } from './FailureReasonModal';
import BranchIcon from '../../assets/svg/BranchIcon';
import Tooltip from '../../components/common/Tooltip/Tooltip';
import { EXTERNAL_LINK_STYLE, ROW_TRUNCATE_LIMIT, SCHEMA_PAIR_TRUNCATE_LIMIT, truncate, truncateUrl } from '../../utils/truncateValues';

// ─── Run history filter options ────────────────────────────────────────────
const STATUS_FILTER_OPTIONS = [
    { label: "All", value: "ALL" },
    { label: "Failed", value: "FAILED" },
    { label: "Completed", value: "SUCCESS" },
    { label: "Cancelled", value: "CANCELLED" },
];

const TIME_FILTER_OPTIONS = [
    { label: "All time", value: "ALL" },
    { label: "Last 1 hour", value: "1H" },
    { label: "Last 6 hours", value: "6H" },
];

const TRIGGER_FILTER_OPTIONS = [
    { label: "All", value: "ALL" },
    { label: "Scheduled", value: "SCHEDULED" },
    { label: "Manual", value: "MANUAL" },
];

// ─── Lightweight single-select dropdown for the run-history filters ──────────
const FilterDropdown = ({ options, value, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const selected = options.find((o) => o.value === value) || options[0];

    return (
        <div className="jrh-filter" ref={ref}>
            <button
                type="button"
                className="jrh-filter-trigger"
                onClick={() => setOpen((prev) => !prev)}
            >
                <span>{selected.label}</span>
                <ChevronDown
                    className={`jrh-filter-chevron ${open ? 'open' : ''}`}
                    style={{ width: "0.625vw", height: "0.625vw" }}
                />
            </button>
            {open && (
                <div className="jrh-filter-menu">
                    {options.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            className={`jrh-filter-option ${opt.value === value ? 'selected' : ''}`}
                            onClick={() => {
                                onChange(opt.value);
                                setOpen(false);
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Animated 1→2→3→1 dot ellipsis, used next to Running / Retrying labels ───
const AnimatedEllipsis = () => {
    const [dotCount, setDotCount] = useState(1);

    useEffect(() => {
        const interval = setInterval(() => {
            setDotCount((prev) => (prev % 3) + 1);
        }, 450);
        return () => clearInterval(interval);
    }, []);

    return (
        <span className="animated-ellipsis" aria-hidden="true">
            <span className={`ellipsis-dot ${dotCount >= 1 ? 'visible' : ''}`}>.</span>
            <span className={`ellipsis-dot ${dotCount >= 2 ? 'visible' : ''}`}>.</span>
            <span className={`ellipsis-dot ${dotCount >= 3 ? 'visible' : ''}`}>.</span>
        </span>
    );
};

const JobRow = ({
    item,
    onToggle,
    onEdit,
    onClone,
    onDelete,
    onStart,
    onStop,
    onDownloadReport,
    isExpanded,
    onExpandToggle,
    onRefreshRun,
    setIsLoading,
}) => {
    const [isJobRunning, setIsJobRunning] = useState(false);
    const [failureModal, setFailureModal] = useState({
        isOpen: false,
        errorData: [],
    });
    // const [isRefreshing, setIsRefreshing] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    // Run History filters (client-side, over loaded runs)
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [timeFilter, setTimeFilter] = useState("ALL");
    const [triggerFilter, setTriggerFilter] = useState("ALL");

    // Check if there's a running (or retrying) job on mount and when runs change
    useEffect(() => {
        if (item.runs && item.runs.length > 0) {
            const hasRunningJob = item.runs.some(
                (run) => run.status === "RUNNING" || run.status === "RETRYING",
            );
            setIsJobRunning(hasRunningJob);
        }
    }, [item.runs]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen]);

    const handleMenuAction = (action) => {
        setMenuOpen(false);
        if (action === 'edit' && onEdit) onEdit(item);
        if (action === 'clone' && onClone) onClone(item);
        if (action === 'delete' && onDelete) onDelete(item.id);
    };

    const handleEdit = () => {
        if (onEdit) {
            onEdit(item);
        }
    };

    const handleStart = () => {
        // setIsJobRunning(true);
        if (onStart) {
            onStart(item.id, () => setIsJobRunning(false));
        }
    };

    const handleStop = () => {
        // setIsJobRunning(false);
        if (onStop) {
            onStop(item.id, () => setIsJobRunning(true));
        }
    };

    const handleRefreshStatus = async (runId) => {
        if (!onRefreshRun) return;
        setIsLoading(true);
        try {
            await onRefreshRun(item.id, runId);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper to format arrays as comma-separated strings
    const formatArray = (arr) => {
        if (!arr || arr.length === 0) return "—";
        return arr.join(", ");
    };

    const OBJECT_TYPE_DISPLAY_NAMES = {
        TABLE: "Standard Table",
        VIEW: "Standard View",
    };

    // Helper to format object types: capitalize, replace underscores, truncate to 2 with tooltip
    const formatObjectTypes = (arr, type = "") => {
        if (!arr || arr.length === 0) return { display: "—", tooltip: "" };
        const formatted = arr.map((item) =>
            type === "schema-pairs-info"
                ? item
                : OBJECT_TYPE_DISPLAY_NAMES[item] ??
                item
                    .toLowerCase()
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (c) => c.toUpperCase()),
        );
        const display =
            formatted.length > 3
                ? formatted.slice(0, 3).join(", ") + "..."
                : formatted.join(", ");
        const tooltip = formatted.join(", ");
        return { display, tooltip };
    };

    // Helper to get initials from job name
    const getInitials = (name) => {
        const words = name.split(" ").filter((w) => w.length > 0);
        if (words.length >= 2) {
            return (words[0][0] + words[1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    // Helper to determine status from runs / job-level status.
    // A currently running run takes precedence so it can still be stopped;
    // otherwise a job-level SCHEDULED status shows the Scheduled pill.
    const getJobStatus = () => {
        const hasRunningRun = item.runs?.some((run) => run.status === "RUNNING");
        if (hasRunningRun) return "Running";
        const hasRetryingRun = item.runs?.some((run) => run.status === "RETRYING");
        if (hasRetryingRun) return "Retrying";
        if (item.status === "SCHEDULED") return "Scheduled";
        return "Ready";
    };

    // Helper to capitalize a status/trigger word (e.g. "MANUAL" -> "Manual")
    const capitalizeWord = (value) =>
        value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "—";

    // Helper to format a duration in seconds as "HHh MMm SSs" (matches run_duration)
    const formatDurationFromSeconds = (seconds) => {
        if (seconds == null) return "—";
        const pad = (n) => String(n).padStart(2, "0");
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        return `${pad(h)}h ${pad(m)}m ${pad(s)}s`;
    };

    // Helper to get latest run info
    const getLatestRunInfo = () => {
        if (!item.runs || item.runs.length === 0) return null;
        return item.runs[0];
    };

    const handleViewSummary = (run) => {
        if (run.status === "FAILED") {
            setFailureModal({ isOpen: true, errorData: run.error_message || [] });
        } else {
            sessionStorage.setItem(
                "summaryReportRunInfo",
                JSON.stringify({ name: item.name, runId: run.id, githubConnectionId: item.github_connection_id }),
            );
            window.open(`/summary-report/${run.id}`, "_blank");
        }
    };

    const latestRun = getLatestRunInfo();
    const initials = getInitials(item.name);
    const jobStatus = getJobStatus();
    const isScheduled = jobStatus === "Scheduled";

    // Currently running or retrying run — target for the header Refresh Status button
    const activeRun = (item.runs || []).find(
        (run) => run.status === "RUNNING" || run.status === "RETRYING",
    );

    const handleHeaderRefresh = () => {
        if (activeRun) handleRefreshStatus(activeRun.id);
    };

    // Filter change: show the shared loader briefly, then apply the client-side filter
    const handleFilterChange = (setter, value) => {
        setIsLoading(true);
        setter(value);
        setTimeout(() => setIsLoading(false), 300);
    };

    // Apply run-history filters (status / time window / trigger type)
    const filteredRuns = (item.runs || []).filter((run) => {
        if (statusFilter !== "ALL" && run.status !== statusFilter) return false;
        if (triggerFilter !== "ALL" && run.triggered_by !== triggerFilter) return false;
        if (timeFilter !== "ALL") {
            if (!run.started_at) return false;
            const startedMs = new Date(run.started_at + "Z").getTime();
            const windowHours = timeFilter === "1H" ? 1 : 6;
            if (Date.now() - startedMs > windowHours * 60 * 60 * 1000) return false;
        }
        return true;
    });

    return (
        <>
            {/* Main Job Row */}
            <div className="job-row">
                <div className="job-main">
                    <button
                        onClick={() => onExpandToggle(item.id)}
                        className="expand-btn"
                    >
                        <ChevronDown
                            className={`expand-icon ${isExpanded ? "expanded" : ""}`}
                            style={{ width: "1.042vw", height: "1.042vw" }} /* 20px */
                        />
                    </button>

                    <div
                        className="job-avatar"
                        style={{
                            background: "linear-gradient(to bottom right, #3b82f6, #a855f7)",
                        }}
                    >
                        {initials}
                    </div>

                    <div className="job-details">
                        <h4 className="job-name">{item.name}</h4>
                        <p className="job-type">Schema Comparison</p>
                        <div className="job-meta">
                            <div className="job-meta-info">
                                {item.github_connection && (
                                    <div className="job-meta-row">
                                        <span className="job-info-item job-info-item--icon">
                                            <Tooltip text="Connection Name"><BranchIcon /></Tooltip>
                                            {/* <span>{item.github_connection.name}</span> */}
                                            {item.github_connection.name.length > ROW_TRUNCATE_LIMIT
                                                ? <Tooltip text={item.github_connection.name} className='repo-details'><span>{truncate(item.github_connection.name)}</span></Tooltip>
                                                : <span>{item.github_connection.name}</span>
                                            }
                                        </span>
                                        <span className="job-info-item job-info-item--icon">
                                            <Tooltip text="Repository Name"><FolderCode width="1.042vw" height="1.042vw" /></Tooltip>
                                            {item.github_connection.repository.length > ROW_TRUNCATE_LIMIT
                                                ? <Tooltip text={item.github_connection.repository} className='repo-details'><span>{truncate(item.github_connection.repository)}</span></Tooltip>
                                                : <span>{item.github_connection.repository}</span>
                                            }
                                        </span>
                                    </div>
                                )}
                                <div className="job-meta-row">
                                    <span className="job-info-item">
                                        Job Created:{" "}
                                        {item.created_at
                                            ? format(
                                                new Date(item.created_at + "Z"),
                                                "yyyy-MM-dd HH:mm:ss",
                                            )
                                            : "—"}
                                    </span>
                                    <span className="job-info-item">
                                        Last Run:{" "}
                                        {latestRun?.started_at
                                            ? format(
                                                new Date(latestRun?.started_at + "Z"),
                                                "yyyy-MM-dd HH:mm:ss",
                                            )
                                            : "—"}
                                    </span>
                                </div>
                            </div>
                            <span className={`job-status-pill ${jobStatus.toLowerCase()}`}>
                                {jobStatus}
                                {(jobStatus === "Running" || jobStatus === "Retrying") && (
                                    <AnimatedEllipsis />
                                )}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="job-actions">
                    {isJobRunning ? (
                        <button className="stop-btn" onClick={handleStop}>
                            STOP
                        </button>
                    ) : (
                        <button
                            className="start-btn"
                            onClick={handleStart}
                            disabled={isScheduled}
                        >
                            START
                        </button>
                    )}
                    {/* <button className="job-edit-btn" onClick={handleEdit}>
            <Edit style={{ width: "1.25vw", height: "1.25vw" }} />
          </button> */}

                    <div className="job-more-menu-wrapper" ref={menuRef}>
                        <button
                            className="job-more-menu-btn"
                            onClick={() => setMenuOpen((prev) => !prev)}
                            aria-label="More options"
                        >
                            <MoreVertical style={{ width: "1.25vw", height: "1.25vw" }} />
                        </button>

                        {menuOpen && (
                            <div className="job-more-menu-dropdown">
                                <button
                                    className="job-more-menu-item"
                                    onClick={() => handleMenuAction("edit")}
                                >
                                    <Edit style={{ width: "0.833vw", height: "0.833vw" }} />
                                    <span>Edit</span>
                                </button>
                                <button
                                    className="job-more-menu-item"
                                    onClick={() => handleMenuAction("clone")}
                                >
                                    <Copy style={{ width: "0.833vw", height: "0.833vw" }} />
                                    <span>Clone</span>
                                </button>
                                {!isJobRunning && (
                                    <button
                                        className="job-more-menu-item job-more-menu-item--delete"
                                        onClick={() => handleMenuAction("delete")}
                                    >
                                        <Trash2 style={{ width: "0.833vw", height: "0.833vw" }} />
                                        <span>Delete</span>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Expanded Job Run History */}
            {isExpanded && (
                <div className="job-runs-container">
                    <div className="runs-header">
                        <h5 className="runs-title">Job Run History</h5>
                        <div className="runs-header-controls">
                            <button
                                className="refresh-status-btn"
                                onClick={handleHeaderRefresh}
                                disabled={!activeRun}
                            >
                                <RefreshCw
                                    style={{ width: "0.781vw", height: "0.781vw" }}
                                />
                                Refresh Status
                            </button>
                            <FilterDropdown
                                options={STATUS_FILTER_OPTIONS}
                                value={statusFilter}
                                onChange={(value) => handleFilterChange(setStatusFilter, value)}
                            />
                            <FilterDropdown
                                options={TIME_FILTER_OPTIONS}
                                value={timeFilter}
                                onChange={(value) => handleFilterChange(setTimeFilter, value)}
                            />
                            <FilterDropdown
                                options={TRIGGER_FILTER_OPTIONS}
                                value={triggerFilter}
                                onChange={(value) => handleFilterChange(setTriggerFilter, value)}
                            />
                        </div>
                    </div>

                    {!item.runs || item.runs.length === 0 ? (
                        <div className="no-runs-message">No run history available</div>
                    ) : filteredRuns.length === 0 ? (
                        <div className="no-runs-message">
                            No runs match the selected filters
                        </div>
                    ) : (
                        <div className="runs-list">
                            {filteredRuns.map((run) => (
                                <div key={run.id} className="run-card">
                                    <div className="run-card-header">
                                        <div className="run-label-group">
                                            <span className="run-id">{run.run_label}</span>
                                            {run.status === "SUCCESS" && run.is_pushed_to_git && (
                                                <Tooltip text="Pushed to Repository" className='repo-push-icon'>
                                                    <span className="run-repo-icon">
                                                        <BranchIcon />
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>

                                    <div className="run-details-grid">
                                        <div className="run-detail">
                                            <span className="detail-label">Start Time:</span>
                                            <span className="detail-value">
                                                {run.started_at
                                                    ? format(
                                                        new Date(run.started_at + "Z"),
                                                        "yyyy-MM-dd HH:mm:ss",
                                                    )
                                                    : "—"}
                                            </span>
                                        </div>

                                        <div className="run-detail">
                                            <span className="detail-label">End Time:</span>
                                            <span className="detail-value">
                                                {run.completed_at
                                                    ? format(
                                                        new Date(run.completed_at + "Z"),
                                                        "yyyy-MM-dd HH:mm:ss",
                                                    )
                                                    : "—"}
                                            </span>
                                        </div>

                                        <div className="run-detail">
                                            <span className="detail-label">Run Duration:</span>
                                            {/* <span className="detail-value detail-value-bold"> */}
                                            <span className="detail-value">
                                                {run.run_duration ? run.run_duration : "—"}
                                            </span>
                                        </div>

                                        <div className="run-detail">
                                            <span className="detail-label">Source Connection:</span>
                                            <span className="detail-value">
                                                {run.source_connection_name}
                                            </span>
                                        </div>

                                        <div className="run-detail">
                                            <span className="detail-label">Target Connection:</span>
                                            <span className="detail-value">
                                                {run.target_connection_name}
                                            </span>
                                        </div>

                                        <div className="run-detail">
                                            <span className="detail-label">Status:</span>
                                            <span className="detail-value">
                                                <span
                                                    className={`run-status-pill ${run.status.toLowerCase()}`}
                                                >
                                                    {run.status === "SUCCESS"
                                                        ? "Completed"
                                                        : run.status.charAt(0).toUpperCase() +
                                                        run.status.slice(1).toLowerCase()}
                                                </span>
                                            </span>
                                        </div>

                                        <div className="run-detail">
                                            <span className="detail-label">
                                                Object Types Compared:
                                            </span>
                                            <span
                                                className="detail-value"
                                                title={
                                                    formatObjectTypes(run.object_types_compared).tooltip
                                                }
                                                style={{
                                                    cursor:
                                                        run.object_types_compared?.length > 3
                                                            ? "help"
                                                            : "default",
                                                }}
                                            >
                                                {formatObjectTypes(run.object_types_compared).display}
                                            </span>
                                        </div>

                                        <div className="run-detail">
                                            <span className="detail-label">Trigger Type:</span>
                                            <span className="detail-value">
                                                {run.triggered_by
                                                    ? capitalizeWord(run.triggered_by)
                                                    : "—"}
                                            </span>
                                        </div>

                                        {/* <div className="run-detail">
                                            <span className="detail-label">Include [prefix]:</span>
                                            <span
                                                className="detail-value"
                                                title={formatObjectTypes(run.include_prefix).tooltip}
                                                style={{ cursor: run.include_prefix?.length > 2 ? 'help' : 'default' }}
                                            >
                                                {formatObjectTypes(run.include_prefix).display}
                                            </span>
                                        </div> */}
                                    </div>

                                    {/* {run.status === 'SUCCESS' && run.has_changes && (
                                        <button
                                            className="download-report-btn"
                                            onClick={() => onDownloadReport?.(run.id, run.run_label)}
                                        >
                                            Download Report
                                        </button>
                                    )} */}

                                    {/* Repository Details Section */}
                                    {run.status === "SUCCESS" && run.is_pushed_to_git && (
                                        <div className="repo-details-section">
                                            <h6 className="repo-details-title">Repository Details</h6>
                                            <div className="repo-details-grid">
                                                <div className="repo-detail-item">
                                                    <span className="repo-detail-label">Connection Name:</span>
                                                    <span className="repo-detail-value repo-detail-value--highlight">
                                                        {/* {run.connection_name || "—"} */}
                                                        {run.connection_name.length > ROW_TRUNCATE_LIMIT
                                                            ? <Tooltip text={run.connection_name} className='repo-details'>{truncate(run.connection_name) || "—"}</Tooltip>
                                                            : run.connection_name
                                                        }
                                                    </span>
                                                </div>
                                                <div className="repo-detail-item">
                                                    <span className="repo-detail-label">Repository Name:</span>
                                                    <span
                                                        className="repo-detail-value repo-detail-value--highlight"
                                                    // title={run.repository_name || undefined}
                                                    >
                                                        {run.repository_name.length > ROW_TRUNCATE_LIMIT
                                                            ? <Tooltip text={run.repository_name} className='repo-details'>{truncate(run.repository_name) || "—"}</Tooltip>
                                                            : run.repository_name
                                                        }
                                                    </span>
                                                </div>
                                                <div className="repo-detail-item">
                                                    <span className="repo-detail-label">Branch Name:</span>
                                                    <span
                                                        className="repo-detail-value repo-detail-value--highlight"
                                                    // title={run.branch_name || run.feature_branch_name || undefined}
                                                    >
                                                        {run.feature_branch_name.length > ROW_TRUNCATE_LIMIT
                                                            ? <Tooltip text={run.feature_branch_name} className='repo-details'>{truncate(run.feature_branch_name) || "—"}</Tooltip>
                                                            : run.feature_branch_name
                                                        }
                                                    </span>
                                                </div>

                                                <div className="repo-detail-item">
                                                    <span className="repo-detail-label">PR URL:</span>
                                                    <span
                                                        className="repo-detail-value repo-detail-value--highlight"
                                                    >
                                                        {run?.pr_link ? (
                                                            <a
                                                                href={run.pr_link}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="jrh-repo-link"
                                                            >
                                                                {run.pr_link.length > ROW_TRUNCATE_LIMIT ? (
                                                                    <Tooltip
                                                                        text={run.pr_link}
                                                                        className="repo-details"
                                                                        direction="left"
                                                                    >
                                                                        {truncateUrl(run.pr_link)}
                                                                        <ExternalLink style={EXTERNAL_LINK_STYLE} />
                                                                    </Tooltip>
                                                                ) : (
                                                                    <>
                                                                        {run.pr_link}
                                                                        <ExternalLink style={EXTERNAL_LINK_STYLE} />
                                                                    </>
                                                                )}
                                                            </a>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </span>
                                                </div>

                                                <div className="repo-detail-item repo-detail-item--full">
                                                    <span className="repo-detail-label">Comments:</span>
                                                    <span className="repo-detail-value repo-detail-value--highlight">{run.comment ?? '—'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Schema Pairs Table */}
                                    {/* {item.schema_pairs && item.schema_pairs.length > 0 && ( */}
                                    {run.schemas_filters_json &&
                                        run.schemas_filters_json.length > 0 && (
                                            <div className="schema-pairs-wrapper">
                                                <table className="schema-pairs-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Schemas Compared</th>
                                                            <th>Include Prefix</th>
                                                            <th>Include Suffix</th>
                                                            <th>Exclude Prefix</th>
                                                            <th>Exclude Suffix</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {run.schemas_filters_json.map((pair) => (
                                                            <tr key={pair.id}>
                                                                <td>
                                                                    {pair.source_schema && pair.source_schema
                                                                        ? (
                                                                            <span className="schema-pair-schemas">
                                                                                {pair.source_schema.length > SCHEMA_PAIR_TRUNCATE_LIMIT
                                                                                    ? <Tooltip text={pair.source_schema} className='repo-details'>
                                                                                        <span className="schema-name">{truncate(pair.source_schema, SCHEMA_PAIR_TRUNCATE_LIMIT)}</span>
                                                                                    </Tooltip>
                                                                                    : <span className="schema-name">{pair.source_schema}</span>
                                                                                }
                                                                                <span className="job-history-arrow-icon">
                                                                                    <ChevronRight
                                                                                        style={{
                                                                                            width: "0.729vw",
                                                                                            height: "0.729vw",
                                                                                            color: "#94a3b8",
                                                                                        }}
                                                                                    />
                                                                                </span>
                                                                                {pair.target_schema.length > SCHEMA_PAIR_TRUNCATE_LIMIT
                                                                                    ? <Tooltip text={pair.target_schema} className='repo-details'>
                                                                                        <span className="schema-name">{truncate(pair.target_schema, SCHEMA_PAIR_TRUNCATE_LIMIT)}</span>
                                                                                    </Tooltip>
                                                                                    : <span className="schema-name">{pair.target_schema}</span>
                                                                                }
                                                                            </span>
                                                                        )
                                                                        : "—"
                                                                    }

                                                                </td>
                                                                <td
                                                                    title={
                                                                        formatObjectTypes(
                                                                            pair.include_prefix,
                                                                            "schema-pairs-info",
                                                                        ).tooltip
                                                                    }
                                                                    style={{
                                                                        cursor:
                                                                            pair.include_prefix?.length > 3
                                                                                ? "help"
                                                                                : "default",
                                                                    }}
                                                                >
                                                                    {
                                                                        formatObjectTypes(
                                                                            pair.include_prefix,
                                                                            "schema-pairs-info",
                                                                        ).display
                                                                    }
                                                                </td>
                                                                <td
                                                                    title={
                                                                        formatObjectTypes(
                                                                            pair.include_suffix,
                                                                            "schema-pairs-info",
                                                                        ).tooltip
                                                                    }
                                                                    style={{
                                                                        cursor:
                                                                            pair.include_suffix?.length > 3
                                                                                ? "help"
                                                                                : "default",
                                                                    }}
                                                                >
                                                                    {
                                                                        formatObjectTypes(
                                                                            pair.include_suffix,
                                                                            "schema-pairs-info",
                                                                        ).display
                                                                    }
                                                                </td>
                                                                <td
                                                                    title={
                                                                        formatObjectTypes(
                                                                            pair.exclude_prefix,
                                                                            "schema-pairs-info",
                                                                        ).tooltip
                                                                    }
                                                                    style={{
                                                                        cursor:
                                                                            pair.exclude_prefix?.length > 3
                                                                                ? "help"
                                                                                : "default",
                                                                    }}
                                                                >
                                                                    {
                                                                        formatObjectTypes(
                                                                            pair.exclude_prefix,
                                                                            "schema-pairs-info",
                                                                        ).display
                                                                    }
                                                                </td>
                                                                <td
                                                                    title={
                                                                        formatObjectTypes(
                                                                            pair.exclude_suffix,
                                                                            "schema-pairs-info",
                                                                        ).tooltip
                                                                    }
                                                                    style={{
                                                                        cursor:
                                                                            pair.exclude_suffix?.length > 3
                                                                                ? "help"
                                                                                : "default",
                                                                    }}
                                                                >
                                                                    {
                                                                        formatObjectTypes(
                                                                            pair.exclude_suffix,
                                                                            "schema-pairs-info",
                                                                        ).display
                                                                    }
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                    {/* Attempt History Table — shown for every Manual and Scheduled job run */}
                                    {run.attempts && run.attempts.length > 0 && (
                                        <div className="attempt-history-section">
                                            <h6 className="attempt-history-title">
                                                Attempt History
                                            </h6>
                                            <div className="schema-pairs-wrapper">
                                                <table className="schema-pairs-table attempt-history-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Attempt</th>
                                                            <th>Start Time</th>
                                                            <th>Duration</th>
                                                            <th>Status</th>
                                                            <th>Execution Summary</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {run.attempts.map((attempt) => (
                                                            <tr key={attempt.id}>
                                                                <td className="attempt-number">
                                                                    #{attempt.attempt_number}
                                                                </td>
                                                                <td>
                                                                    {attempt.started_at
                                                                        ? format(
                                                                            new Date(attempt.started_at + "Z"),
                                                                            "yyyy-MM-dd HH:mm:ss",
                                                                        )
                                                                        : "—"}
                                                                </td>
                                                                <td>
                                                                    {formatDurationFromSeconds(
                                                                        attempt.duration_seconds,
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <span
                                                                        className={`run-status-pill ${attempt.status.toLowerCase()}`}
                                                                    >
                                                                        {attempt.status === "SUCCESS"
                                                                            ? "Completed"
                                                                            : capitalizeWord(attempt.status)}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {attempt.status === "FAILED" ? (
                                                                        <button
                                                                            type="button"
                                                                            className="execution-summary-link"
                                                                            onClick={() => handleViewSummary(attempt)}
                                                                        >
                                                                            {getFailureSummaryLabel(attempt.error_message)}
                                                                            <ExternalLink style={EXTERNAL_LINK_STYLE} />
                                                                        </button>
                                                                    ) : attempt.status === "SUCCESS" && run.has_changes ? (
                                                                        <button
                                                                            type="button"
                                                                            className="execution-summary-link"
                                                                            onClick={() => handleViewSummary(run)}
                                                                        >
                                                                            View Summary
                                                                            <ExternalLink style={EXTERNAL_LINK_STYLE} />
                                                                        </button>
                                                                    ) : attempt.status === "SUCCESS" ? (
                                                                        <span className="execution-summary-no-change">No Change</span>
                                                                    ) : (
                                                                        "—"
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Failure Reason Modal */}
            <FailureReasonModal
                isOpen={failureModal.isOpen}
                onClose={() => setFailureModal({ isOpen: false, errorData: [] })}
                errorData={failureModal.errorData}
            />
        </>
    );
};

export default JobRow;